import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { GraduationCap, Plus, CheckCircle2, Users } from "lucide-react";

export const AUDIENCE = { tutti: "Tutti", operativo: "Squadre operative", ufficio: "Ufficio" };

function CourseCard({ c, isAdmin }) {
  const p = c.progress;
  const total = c.lessons?.length || 0;
  const done = p ? new Set(p.completed_lessons).size : 0;
  return (
    <Link to={`/formazione/${c.id}`} className="block bg-white rounded-2xl border border-slate-200 p-5 hover:border-sky-300 hover:-translate-y-0.5 transition-transform" data-testid={`course-card-${c.id}`}>
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-display font-bold text-slate-900 text-lg">{c.title}</h3>
        {c.mandatory && <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 shrink-0">Obbligatorio</span>}
      </div>
      <p className="text-sm text-slate-500 mt-1 line-clamp-2">{c.description}</p>
      <div className="mt-4 flex flex-wrap items-center gap-3 text-xs text-slate-600">
        <span>{total} lezioni · {c.quiz?.length || 0} domande</span>
        {isAdmin ? (
          <>
            <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {AUDIENCE[c.audience]}</span>
            <span className="font-semibold text-sky-700">{c.passed_count}/{c.audience_count} superato</span>
            {!c.published && <span className="font-bold text-amber-700">BOZZA</span>}
          </>
        ) : p?.passed ? (
          <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold"><CheckCircle2 className="w-4 h-4" /> Superato</span>
        ) : (
          <span className="font-semibold text-sky-700">{done}/{total} lezioni fatte</span>
        )}
      </div>
    </Link>
  );
}

export default function Training() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const isAdmin = user?.role === "admin";
  useEffect(() => { api.get("/courses").then((r) => setList(r.data)); }, []);
  const createSafety = async () => {
    try { const { data } = await api.post("/courses/templates/safety"); navigate(`/formazione/${data.id}/modifica`); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <div className="space-y-6" data-testid="training-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-sky-600 font-semibold mb-2">Formazione</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Corsi</h1>
          <p className="text-slate-500 mt-2 text-sm">{isAdmin ? "Crea corsi con lezioni, video, PDF e quiz finale. Segui i progressi di ogni dipendente." : "Segui le lezioni, supera il quiz e scarica il tuo attestato."}</p>
        </div>
        {isAdmin && (
          <div className="flex flex-wrap gap-2">
            {!list.some((c) => c.template_key === "sicurezza-trasloco") && (
              <Button variant="outline" onClick={createSafety} className="h-11" data-testid="create-safety-course-button">
                <GraduationCap className="w-4 h-4 mr-2" /> Corso pronto: Sicurezza nel trasloco
              </Button>
            )}
            <Button onClick={() => navigate("/formazione/nuovo/modifica")} className="bg-sky-600 hover:bg-sky-700 text-white h-11" data-testid="new-course-button">
              <Plus className="w-4 h-4 mr-2" /> Nuovo corso
            </Button>
          </div>
        )}
      </header>
      {list.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">
          <GraduationCap className="w-10 h-10 mx-auto mb-3" /> Nessun corso disponibile.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="courses-list">
          {list.map((c) => <CourseCard key={c.id} c={c} isAdmin={isAdmin} />)}
        </div>
      )}
    </div>
  );
}
